/** Piezas comunes del panel de administración: tarjetas, variaciones, estados y paginación. */
import type { ReactNode } from 'react'
import type { AdminComparado, AdminEstadoUsuario } from '@eco/shared'
import { Minigrafico } from '../../components/charts'
import {
  ESTADO_USUARIO_LABEL,
  textoVariacion,
  tonoVariacion,
  variacion
} from '../../lib/adminFormat'

/** Variación contra el período anterior, con color según si subir es bueno. */
export function Delta({
  comparado,
  subirEsBueno = true
}: {
  comparado: AdminComparado
  subirEsBueno?: boolean
}) {
  const v = variacion(comparado.valor, comparado.anterior)
  const tono = tonoVariacion(v, subirEsBueno)
  // El signo lleva la dirección; el color, si es bueno o malo (nunca solo el color)
  return (
    <span className={`delta ${tono}`} title="Contra el período anterior del mismo largo">
      {textoVariacion(v)}
    </span>
  )
}

/** Tarjeta de una cifra: etiqueta, valor, variación y tendencia opcionales. */
export function Kpi({
  label,
  valor,
  comparado,
  subirEsBueno,
  tendencia,
  nota
}: {
  label: string
  valor: ReactNode
  comparado?: AdminComparado
  subirEsBueno?: boolean
  tendencia?: number[]
  nota?: ReactNode
}) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-valor">{valor}</div>
      <div className="kpi-pie">
        {comparado && <Delta comparado={comparado} subirEsBueno={subirEsBueno} />}
        {nota && <span className="faint">{nota}</span>}
        <span className="grow" />
        {tendencia && <Minigrafico valores={tendencia} etiqueta={`Tendencia de ${label}`} />}
      </div>
    </div>
  )
}

export function EstadoUsuario({ estado }: { estado: AdminEstadoUsuario }) {
  return <span className={`estado-usuario ${estado}`}>{ESTADO_USUARIO_LABEL[estado]}</span>
}

export const PLAN_LABEL = { free: 'Gratis', personal: 'Personal', equipo: 'Equipo' } as const

/** Selector de período (7, 30 o 90 días). */
export function Periodo({ dias, onChange }: { dias: number; onChange: (d: number) => void }) {
  return (
    <select value={dias} onChange={(e) => onChange(Number(e.target.value))} aria-label="Período">
      <option value={7}>Últimos 7 días</option>
      <option value={30}>Últimos 30 días</option>
      <option value={90}>Últimos 90 días</option>
    </select>
  )
}

/** «1–50 de 312» con anterior y siguiente. */
export function Paginador({
  desde,
  cantidad,
  total,
  limite,
  onCambiar
}: {
  desde: number
  cantidad: number
  total: number
  limite: number
  onCambiar: (desplazamiento: number) => void
}) {
  if (total === 0) return null
  return (
    <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
      <span className="faint" style={{ fontSize: 12 }}>
        {cantidad ? `${desde + 1}–${desde + cantidad}` : '0'} de {total.toLocaleString('es-CL')}
      </span>
      <button
        className="btn sm"
        disabled={desde === 0}
        onClick={() => onCambiar(Math.max(0, desde - limite))}
      >
        Anterior
      </button>
      <button
        className="btn sm"
        disabled={desde + limite >= total}
        onClick={() => onCambiar(desde + limite)}
      >
        Siguiente
      </button>
    </div>
  )
}

/** Tarjeta con título para las secciones del panel. */
export function Seccion({
  titulo,
  acciones,
  children,
  className = ''
}: {
  titulo: string
  acciones?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`card stack ${className}`}>
      <div className="row">
        <h2 className="grow" style={{ margin: 0, fontSize: 15 }}>
          {titulo}
        </h2>
        {acciones}
      </div>
      {children}
    </section>
  )
}
